import { useEffect, useRef, useState, createElement, type ComponentType } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Compartment, EditorState, Prec, RangeSetBuilder, StateEffect, StateField } from "@codemirror/state";
import { Decoration, EditorView, WidgetType, keymap, placeholder as cmPlaceholder } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { defaultHighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { Asterisk, Divide, Equal, Minus, Plus, type LucideProps } from "lucide-react";
import { cn } from "@patient-watch/ui";
import { FORMULA_FUNCTIONS } from "./hiddenFieldFormula";

export interface FormulaQuestionOption {
    input_id: string;
    label: string;
    detail?: string;
}

interface FormulaEditorProps {
    value: string;
    onChange: (next: string) => void;
    questions: FormulaQuestionOption[];
    placeholder?: string;
    ariaLabel?: string;
    testId?: string;
}

const REF_PATTERN = /\{\{\s*([0-9a-fA-F-]{36}|[A-Za-z0-9_-]+)\s*\}\}/g;
const FUNCTION_PATTERN = new RegExp(`\\b(${FORMULA_FUNCTIONS.map((fn) => fn.name).join("|")})\\b`, "gi");
const QUERY_PATTERN = /[A-Za-z0-9_][A-Za-z0-9_ -]*$/;

/** Pale Tailwind-100 tones, one stable colour per question id. */
const PILL_TONES = [
    { chip: "bg-sky-100 text-sky-800", background: "#e0f2fe", color: "#075985" },
    { chip: "bg-emerald-100 text-emerald-800", background: "#d1fae5", color: "#065f46" },
    { chip: "bg-amber-100 text-amber-800", background: "#fef3c7", color: "#92400e" },
    { chip: "bg-rose-100 text-rose-800", background: "#ffe4e6", color: "#9f1239" },
    { chip: "bg-violet-100 text-violet-800", background: "#ede9fe", color: "#5b21b6" },
    { chip: "bg-teal-100 text-teal-800", background: "#ccfbf1", color: "#115e59" },
    { chip: "bg-orange-100 text-orange-800", background: "#ffedd5", color: "#9a3412" },
    { chip: "bg-cyan-100 text-cyan-800", background: "#cffafe", color: "#155e75" },
    { chip: "bg-lime-100 text-lime-800", background: "#ecfccb", color: "#3f6212" },
    { chip: "bg-fuchsia-100 text-fuchsia-800", background: "#fae8ff", color: "#86198f" },
    { chip: "bg-indigo-100 text-indigo-800", background: "#e0e7ff", color: "#3730a3" },
    { chip: "bg-pink-100 text-pink-800", background: "#fce7f3", color: "#9d174d" },
] as const;

export function pillTone(id: string) {
    let hash = 0;
    for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
    return PILL_TONES[hash % PILL_TONES.length] ?? PILL_TONES[0];
}

/**
 * Where a question or function can be inserted.
 * Inside SUM( | ), COUNT( | ) and IF( | ) the text before the cursor ends in "("
 * so there is no word to match - that still has to offer the variable list.
 */
function insertionAt(before: string): { query: string; localFrom: number } | null {
    const openBrace = before.lastIndexOf("{{");
    const closeBrace = before.lastIndexOf("}}");
    if (openBrace > closeBrace) return null;

    const ident = QUERY_PATTERN.exec(before);
    if (ident?.[0] != null && ident.index != null && !/^\d+(\.\d+)?$/.test(ident[0])) {
        return { query: ident[0], localFrom: ident.index };
    }

    const trimmed = before.replace(/\s+$/, "");
    if (trimmed === "" || /[(,+\-*/<>=!]$/.test(trimmed)) {
        return { query: "", localFrom: before.length };
    }
    return null;
}

class QuestionPillWidget extends WidgetType {
    constructor(
        readonly label: string,
        readonly detail: string,
        readonly tone: (typeof PILL_TONES)[number]
    ) {
        super();
    }

    eq(other: QuestionPillWidget): boolean {
        return (
            other instanceof QuestionPillWidget &&
            other.label === this.label &&
            other.detail === this.detail &&
            other.tone.background === this.tone.background
        );
    }

    toDOM(): HTMLElement {
        const pill = document.createElement("span");
        pill.className = "cm-formula-token cm-formula-pill";
        pill.textContent = this.label;
        pill.title = this.detail;
        pill.style.background = this.tone.background;
        pill.style.color = this.tone.color;
        return pill;
    }

    ignoreEvent(): boolean {
        return false;
    }
}

const OPERATOR_ICONS: Record<string, { icon: ComponentType<LucideProps>; label: string }> = {
    "+": { icon: Plus, label: "Plus" },
    "-": { icon: Minus, label: "Minus" },
    "*": { icon: Asterisk, label: "Times" },
    "/": { icon: Divide, label: "Divide" },
    "=": { icon: Equal, label: "Equals" },
};

class SymbolWidget extends WidgetType {
    constructor(readonly operator: string) {
        super();
    }

    eq(other: SymbolWidget): boolean {
        return other instanceof SymbolWidget && other.operator === this.operator;
    }

    toDOM(): HTMLElement {
        const symbol = document.createElement("span");
        const operator = OPERATOR_ICONS[this.operator];
        symbol.className = "cm-formula-token cm-formula-symbol";
        symbol.setAttribute("role", "img");
        symbol.setAttribute("aria-label", operator?.label ?? this.operator);
        if (operator) {
            symbol.innerHTML = renderToStaticMarkup(
                createElement(operator.icon, { size: 14, strokeWidth: 2.5, "aria-hidden": true })
            );
        }
        return symbol;
    }

    ignoreEvent(): boolean {
        return true;
    }
}

const refreshPillsEffect = StateEffect.define<void>();

function buildFormulaDecorations(docText: string, questions: FormulaQuestionOption[]) {
    const builder = new RangeSetBuilder<Decoration>();
    const byId = new Map(questions.map((question) => [question.input_id, question]));
    const refRanges: Array<{ from: number; to: number }> = [];
    const marks: Array<{ from: number; to: number; decoration: Decoration }> = [];

    for (const match of docText.matchAll(REF_PATTERN)) {
        const full = match[0];
        const id = match[1];
        const from = match.index ?? 0;
        const to = from + full.length;
        refRanges.push({ from, to });
        if (!id) continue;
        const question = byId.get(id);
        const label = question?.label?.trim() ? question.label.trim().slice(0, 28) : id.slice(0, 8);
        const detail = question
            ? `${question.detail ? `${question.detail} - ` : ""}${question.label}`
            : `Unknown question ${id}`;
        marks.push({
            from,
            to,
            decoration: Decoration.replace({
                widget: new QuestionPillWidget(label, detail, pillTone(id)),
                inclusive: false,
            }),
        });
    }

    // Colour function keywords outside question pills.
    for (const match of docText.matchAll(FUNCTION_PATTERN)) {
        const from = match.index ?? 0;
        const to = from + match[0].length;
        if (refRanges.some((range) => from >= range.from && from < range.to)) continue;
        marks.push({
            from,
            to,
            decoration: Decoration.replace({
                widget: new TextTokenWidget(match[0], "cm-formula-token cm-formula-fn"),
                inclusive: false,
            }),
        });
    }

    const isInsideRef = (index: number) => refRanges.some((range) => index >= range.from && index < range.to);
    for (let index = 0; index < docText.length; index += 1) {
        const char = docText[index] ?? "";
        if (!(char in OPERATOR_ICONS) || isInsideRef(index)) continue;
        marks.push({
            from: index,
            to: index + 1,
            decoration: Decoration.replace({
                widget: new SymbolWidget(char),
                inclusive: false,
            }),
        });
    }

    for (const match of docText.matchAll(/\d+\.?\d*|\.\d+/g)) {
        const from = match.index ?? 0;
        const to = from + match[0].length;
        if (refRanges.some((range) => from < range.to && to > range.from)) continue;
        marks.push({
            from,
            to,
            decoration: Decoration.replace({
                widget: new TextTokenWidget(match[0], "cm-formula-token cm-formula-number"),
                inclusive: false,
            }),
        });
    }

    // CodeMirror requires decorations in document order. Operators and SUM/IF/COUNT
    // often sit before a later question pill, so add them only after sorting.
    marks.sort((a, b) => a.from - b.from || a.to - b.to);
    for (const mark of marks) builder.add(mark.from, mark.to, mark.decoration);
    return builder.finish();
}

type Suggestion =
    | { kind: "question"; key: string; label: string; detail: string; inputId: string }
    | { kind: "function"; key: string; name: string; hint: string; snippet: string };

function getSuggestions(query: string, questions: FormulaQuestionOption[]): Suggestion[] {
    const needle = query.trim().toLowerCase();
    const matchesQuestion = (question: FormulaQuestionOption) =>
        !needle ||
        question.label.toLowerCase().includes(needle) ||
        (question.detail ?? "").toLowerCase().includes(needle) ||
        question.input_id.toLowerCase().includes(needle);
    const matchedQuestions: Suggestion[] = questions
        .filter(matchesQuestion)
        .slice(0, 8)
        .map((question) => ({
            kind: "question" as const,
            key: `q-${question.input_id}`,
            label: question.label,
            detail: question.detail ?? "Question",
            inputId: question.input_id,
        }));
    const matchedFunctions: Suggestion[] = FORMULA_FUNCTIONS.filter(
        (fn) => !needle || fn.name.toLowerCase().startsWith(needle) || fn.hint.toLowerCase().includes(needle)
    ).map((fn) => ({
        kind: "function" as const,
        key: `fn-${fn.name}`,
        name: fn.name,
        hint: fn.hint,
        snippet: fn.snippet,
    }));
    return [...matchedQuestions, ...matchedFunctions];
}

class TextTokenWidget extends WidgetType {
    constructor(
        readonly text: string,
        readonly className: string
    ) {
        super();
    }

    eq(other: TextTokenWidget): boolean {
        return other instanceof TextTokenWidget && other.text === this.text && other.className === this.className;
    }

    toDOM(): HTMLElement {
        const token = document.createElement("span");
        token.className = this.className;
        token.textContent = this.text;
        return token;
    }

    ignoreEvent(): boolean {
        return true;
    }
}

const formulaTheme = EditorView.theme({
    "&": { fontSize: "14px" },
    ".cm-content": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", padding: "8px 12px" },
    ".cm-line": { lineHeight: "32px" },
    ".cm-formula-token": {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        height: "24px",
        margin: "0 2px",
        padding: "0 8px",
        fontSize: "14px",
        fontWeight: "600",
        lineHeight: "24px",
        verticalAlign: "middle",
    },
    ".cm-formula-pill": {
        borderRadius: "6px",
        whiteSpace: "nowrap",
        maxWidth: "200px",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    ".cm-formula-fn": { color: "#7c3aed", fontWeight: "700" },
    ".cm-formula-symbol": { fontWeight: "700" },
    ".cm-formula-symbol svg": { display: "block", width: "14px", height: "14px" },
});

const FUNCTION_BADGE_STYLES: Record<string, string> = {
    SUM: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    AVERAGE: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
    MIN: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    MAX: "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
    COUNT: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    ROUND: "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300",
    ABS: "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
    IF: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
};

export function FormulaEditor({
    value,
    onChange,
    questions,
    placeholder = "Type a question name or SUM(…)",
    ariaLabel = "Custom formula",
    testId,
}: FormulaEditorProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<EditorView | null>(null);
    const onChangeRef = useRef(onChange);
    const questionsRef = useRef(questions);
    const [failed, setFailed] = useState(false);
    const [suggest, setSuggest] = useState<{ query: string; from: number; to: number } | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const extensionsCompartment = useRef(new Compartment());

    onChangeRef.current = onChange;
    questionsRef.current = questions;

    const suggestions = suggest ? getSuggestions(suggest.query, questions) : [];
    const panelVisible = suggest != null && !failed;

    const applySuggestion = (item: Suggestion) => {
        const view = viewRef.current;
        if (!view || !suggest) return;
        if (item.kind === "question") {
            const insert = `{{${item.inputId}}} `;
            view.dispatch({
                changes: { from: suggest.from, to: suggest.to, insert },
                selection: { anchor: suggest.from + insert.length },
            });
        } else {
            const insert = `${item.name}()`;
            view.dispatch({
                changes: { from: suggest.from, to: suggest.to, insert },
                selection: { anchor: suggest.from + item.name.length + 1 },
            });
        }
        view.focus();
    };

    const keyActionsRef = useRef({
        move: (_delta: number) => false,
        accept: () => false,
        close: () => false,
    });
    keyActionsRef.current = {
        move: (delta: number) => {
            if (!panelVisible || suggestions.length === 0) return false;
            setActiveIndex((current) => (current + delta + suggestions.length) % suggestions.length);
            return true;
        },
        accept: () => {
            if (!panelVisible || suggestions.length === 0) return false;
            const item = suggestions[Math.min(activeIndex, suggestions.length - 1)];
            if (!item) return false;
            applySuggestion(item);
            return true;
        },
        close: () => {
            if (!panelVisible) return false;
            setSuggest(null);
            return true;
        },
    };

    useEffect(() => {
        setActiveIndex(0);
    }, [suggest?.query, suggestions.length]);

    useEffect(() => {
        panelRef.current?.querySelector('[data-suggestion-active="true"]')?.scrollIntoView({ block: "nearest" });
    }, [activeIndex]);

    // Push question label changes into the pill decorations.
    useEffect(() => {
        viewRef.current?.dispatch({ effects: refreshPillsEffect.of(undefined) });
    }, [questions]);

    // Keep CodeMirror in sync when the stored expression changes elsewhere.
    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        const current = view.state.doc.toString();
        if (current !== value) {
            view.dispatch({ changes: { from: 0, to: current.length, insert: value } });
        }
    }, [value]);

    useEffect(() => {
        const parent = containerRef.current;
        if (!parent || viewRef.current) return;
        try {
            const formulaField = StateField.define({
                create(state) {
                    return buildFormulaDecorations(state.doc.toString(), questionsRef.current);
                },
                update(decorations, transaction) {
                    let rebuild = transaction.docChanged;
                    for (const effect of transaction.effects) {
                        if (effect.is(refreshPillsEffect)) rebuild = true;
                    }
                    if (rebuild) return buildFormulaDecorations(transaction.newDoc.toString(), questionsRef.current);
                    return decorations.map(transaction.changes);
                },
                provide: (field) => EditorView.decorations.from(field),
            });

            const readQuery = (view: EditorView) => {
                const head = view.state.selection.main.head;
                const line = view.state.doc.lineAt(head);
                const before = line.text.slice(0, head - line.from);
                onChangeRef.current(view.state.doc.toString());
                const slot = insertionAt(before);
                if (!slot) {
                    setSuggest((current) => (current == null ? current : null));
                    return;
                }
                setSuggest((current) => {
                    const from = line.from + slot.localFrom;
                    if (current?.query === slot.query && current.from === from && current.to === head) return current;
                    return { query: slot.query, from, to: head };
                });
            };

            const state = EditorState.create({
                doc: value,
                extensions: [
                    extensionsCompartment.current.of([]),
                    history(),
                    Prec.high(
                        keymap.of([
                            {
                                key: "ArrowDown",
                                run: () => keyActionsRef.current.move(1),
                            },
                            {
                                key: "ArrowUp",
                                run: () => keyActionsRef.current.move(-1),
                            },
                            {
                                key: "Enter",
                                run: () => {
                                    if (keyActionsRef.current.accept()) return true;
                                    (viewRef.current?.contentDOM as HTMLElement | undefined)?.blur();
                                    return true;
                                },
                            },
                            {
                                key: "Tab",
                                run: () => keyActionsRef.current.accept(),
                            },
                            {
                                key: "Escape",
                                run: () => keyActionsRef.current.close(),
                            },
                        ])
                    ),
                    keymap.of([...defaultKeymap, ...historyKeymap]),
                    syntaxHighlighting(defaultHighlightStyle),
                    EditorView.lineWrapping,
                    cmPlaceholder(placeholder),
                    EditorView.updateListener.of((update) => {
                        if (update.docChanged || update.selectionSet) readQuery(update.view);
                    }),
                    EditorView.contentAttributes.of({ "aria-label": ariaLabel }),
                    formulaField,
                    formulaTheme,
                ],
            });
            const view = new EditorView({ state, parent });
            viewRef.current = view;
            return () => {
                view.destroy();
                viewRef.current = null;
            };
        } catch {
            setFailed(true);
            return;
        }
        // Mount once; live updates flow through refs and effects above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (failed) {
        return (
            <textarea
                aria-label={ariaLabel}
                data-testid={testId}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
                className="bg-background px-3 py-2 border border-input rounded-md w-full min-h-9 font-mono text-sm"
                rows={2}
            />
        );
    }

    const questionSuggestions = suggestions.filter((item) => item.kind === "question");
    const functionSuggestions = suggestions.filter((item) => item.kind === "function");
    let rowIndex = -1;

    return (
        <div
            data-testid={testId}
            className="bg-background border border-input rounded-md focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0 w-full overflow-hidden"
        >
            <div ref={containerRef} className="font-mono" />
            {panelVisible ? (
                <div
                    ref={panelRef}
                    role="listbox"
                    aria-label="Formula suggestions"
                    className="bg-background py-1 border-input border-t max-h-64 overflow-y-auto"
                >
                    <p className="px-3 pt-2 pb-1 font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                        Questions
                    </p>
                    {questionSuggestions.length === 0 ? (
                        <p className="px-3 py-2 text-muted-foreground text-sm">
                            {questions.length === 0
                                ? "Add a question to the form to use it here."
                                : "No question matches that."}
                        </p>
                    ) : null}
                    {questionSuggestions.map((item) => {
                        rowIndex += 1;
                        const index = rowIndex;
                        const active = index === activeIndex;
                        if (item.kind !== "question") return null;
                        return (
                            <button
                                key={item.key}
                                type="button"
                                role="option"
                                aria-selected={active}
                                data-suggestion-active={active}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    applySuggestion(item);
                                }}
                                onMouseEnter={() => setActiveIndex(index)}
                                className={cn(
                                    "flex items-center gap-2 px-3 py-2 w-full text-sm text-left",
                                    active ? "bg-accent text-accent-foreground" : "text-foreground"
                                )}
                            >
                                <span
                                    className={cn(
                                        "px-2 py-0.5 rounded-md max-w-44 font-semibold text-xs truncate shrink-0",
                                        pillTone(item.inputId).chip
                                    )}
                                >
                                    {item.label}
                                </span>
                                <span className="flex-1 min-w-0 text-muted-foreground text-xs truncate">
                                    {item.detail}
                                </span>
                            </button>
                        );
                    })}
                    {functionSuggestions.length > 0 ? (
                        <p className="px-3 pt-2 pb-1 font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                            Functions
                        </p>
                    ) : null}
                    {functionSuggestions.map((item) => {
                        rowIndex += 1;
                        const index = rowIndex;
                        const active = index === activeIndex;
                        if (item.kind !== "function") return null;
                        return (
                            <button
                                key={item.key}
                                type="button"
                                role="option"
                                aria-selected={active}
                                data-suggestion-active={active}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    applySuggestion(item);
                                }}
                                onMouseEnter={() => setActiveIndex(index)}
                                className={cn(
                                    "flex items-center gap-2 px-3 py-2 w-full text-sm text-left",
                                    active ? "bg-accent text-accent-foreground" : "text-foreground"
                                )}
                            >
                                <span
                                    className={cn(
                                        "px-2 py-0.5 rounded-md font-mono font-bold text-xs shrink-0",
                                        FUNCTION_BADGE_STYLES[item.name] ?? "bg-muted text-foreground"
                                    )}
                                >
                                    {item.snippet}
                                </span>
                                <span className="flex-1 min-w-0 text-muted-foreground text-xs truncate">
                                    {item.hint}
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
